// Settings Config Merger - Combines client and backend configurations into unified structure
// Central coordination of client and backend settings for the settings UI
// NOTE : This shared utility can and SHOULD be used by both web and electron; 
//      : assuming backend options are needed at the web level ie with the addition of an admin panel / role

import { SettingConfig } from '../../types/settingsTypes';

// Merges client and backend configs into unified structure for the UI
// Avoids duplicates and maintains category organization for the settings tabs
export const _oldGetSettingsConfig = (
    clientSettingsConfig: Record<string, SettingConfig[]>,
    backendOptionsConfig: Record<string, SettingConfig[]>,
): Record<string, SettingConfig[]> => {
    const mergedConfig: Record<string, SettingConfig[]> = {};
    
    // Merge client settings
    for (const [category, settings] of Object.entries(clientSettingsConfig)) {
        mergedConfig[category] = [...(mergedConfig[category] || []), ...settings];
    }
    
    // Merge backend settings (avoiding duplicates by key)
    for (const [category, settings] of Object.entries(backendOptionsConfig)) {
        if (!mergedConfig[category]) mergedConfig[category] = [];
        for (const setting of settings) {
            if (!mergedConfig[category].some(s => s.key === setting.key)) {
                mergedConfig[category].push(setting);
            }
        }
    }
    
    return mergedConfig;
};

// Blind merge of settings configs, no filtering or deduplication
// Used for any set of configs
// Generic to support both SettingConfig and UserSettingConfig
export const mergeSettingsConfig = <T extends SettingConfig = SettingConfig>(
    ...configs: Record<string, T[]>[]
): Record<string, T[]> => {
    const merged: Record<string, T[]> = {};

    for (const config of configs) {
        for (const [category, settings] of Object.entries(config)) {
            if (!merged[category]) merged[category] = [];
            for (const setting of settings) {
                if (!merged[category].some(s => s.key === setting.key)) {
                    merged[category].push(setting);
                }
            }
        }
    }

    return merged;
};

// Helper function to merge settings configs
// Allows for plaform agnostic merging of settings configs
// ie. Client + Backend || User + Backend || Client + User + Backend
export const getSettingsConfig = (
    clientConfig: Record<string, SettingConfig[]>,
    backendConfig: Record<string, SettingConfig[]>
): Record<string, SettingConfig[]> => {
    return mergeSettingsConfig(clientConfig, backendConfig);
};